// src/utils/openHours.ts
//
// Compute "is this place open right now?" from the cached
// opening_hours.periods array we get from Google Places. Lets us
// honor the open_now filter without an extra Place Details API
// call inside the cache window — the periods array is good for
// the duration of the cache (1h for standard, 5min for open-now-
// intent searches), and we re-evaluate locally on every render.
//
// Periods schema (Google's):
//
//   periods: [
//     { open:  { day: 1, time: "0900" },
//       close: { day: 1, time: "2200" } },
//     ...
//   ]
//
// day: 0 = Sunday, 1 = Monday, … 6 = Saturday.
// time: "HHMM" 24-hour, in the place's local time.
//
// A period without `close` means "open 24/7 starting at open.time
// every week" (rare; ATMs, gas stations).
//
// LIMITATION: We treat the user's local time as if it matches the
// place's local time. That's true 99% of the time when looking at
// "places near me." If you ever surface places in a different
// timezone (rare in this app's UX), the open-now indicator will
// be off by the timezone delta.

interface Period {
  open?: { day?: number; time?: string };
  close?: { day?: number; time?: string };
}

/** Convert a HHMM string to minutes-since-midnight. Returns null
 *  for malformed input. */
function parseHHMM(t: string | undefined): number | null {
  if (!t || t.length !== 4) return null;
  const h = Number(t.slice(0, 2));
  const m = Number(t.slice(2, 4));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

/** Day-of-week (0..6, Sun..Sat) and minutes-since-midnight for
 *  the current local time. Exposed for unit testing — the main
 *  isOpenNow function calls this internally. */
export function nowDayMinutes(now: Date = new Date()): {
  day: number;
  minutes: number;
} {
  return {
    day: now.getDay(),
    minutes: now.getHours() * 60 + now.getMinutes(),
  };
}

/** Returns true iff at least one period in `periods` covers the
 *  given (day, minutes). Returns null when we can't tell — empty
 *  periods, all-malformed periods. The caller decides how to
 *  treat null (we display it as "hours unknown").
 *
 *  Special-case: a period with `open` but no `close` is treated
 *  as "open from open.time every day forever" — the canonical
 *  Google representation of 24/7. */
export function isOpenNow(
  periods: Period[] | undefined | null,
  now: Date = new Date()
): boolean | null {
  if (!Array.isArray(periods) || periods.length === 0) return null;

  const cur = nowDayMinutes(now);
  let anyValidPeriod = false;

  for (const p of periods) {
    if (!p?.open) continue;
    const openDay = p.open.day;
    const openMin = parseHHMM(p.open.time);
    if (typeof openDay !== 'number' || openMin === null) continue;
    anyValidPeriod = true;

    // 24/7: open with no close ⇒ always open.
    if (!p.close) return true;

    const closeDay = p.close.day;
    const closeMin = parseHHMM(p.close.time);
    if (typeof closeDay !== 'number' || closeMin === null) continue;

    // Convert both endpoints to "minutes since Sunday 00:00" so
    // we can compare ranges that cross midnight or even day
    // boundaries (e.g. a bar that opens Friday 9pm and closes
    // Saturday 3am). Same conversion for the current time.
    const openTotal = openDay * 24 * 60 + openMin;
    let closeTotal = closeDay * 24 * 60 + closeMin;
    // If close is "before" open in week-coordinates, the period
    // wraps the week boundary (e.g. Sat 22:00 → Sun 02:00).
    // Push close forward one week so the range stays monotonic.
    if (closeTotal <= openTotal) closeTotal += 7 * 24 * 60;

    // Compare the current week-time against the period in two
    // possible alignments (this week + last week wrapping into
    // this week) so a Sun-early-morning open that started
    // Saturday night still matches.
    const curTotal = cur.day * 24 * 60 + cur.minutes;
    const curTotalNextWeek = curTotal + 7 * 24 * 60;
    if (
      (curTotal >= openTotal && curTotal < closeTotal) ||
      (curTotalNextWeek >= openTotal && curTotalNextWeek < closeTotal)
    ) {
      return true;
    }
  }

  return anyValidPeriod ? false : null;
}
